import { appPath } from '@featit/shared/addressing/paths';

/**
 * Every route a browser may be on with no session (I-0942): the doors before an account, and the
 * public token routes a reporter, a shared link or an invitation opens without one.
 *
 * `App.tsx`'s route table draws each of these under `PublicLayout` — this is the one place both that
 * table and the global 401 handler (`ApiService.loginRedirectTarget`) read, so a route added to one
 * and not the other is caught by `publicRoutes.guard.test.ts` rather than by a reporter losing what
 * they typed.
 */
export const PUBLIC_ROUTE_PATHS = [
  '/connect',
  // F-0518: the desktop app's first screen, drawn before any account exists on this install.
  '/welcome',
  // F-0581 OB-1: Get started, ahead of `/welcome` and `/login` on a first start.
  '/get-started',
  '/login',
  '/register',
  '/cli-auth',
  // F-0819: the page itself sends a signed-out visitor to sign in and back.
  '/desktop-auth',
  '/mobile-auth',
  // I-0775: the desktop's passkey ceremony page, opened in the system sheet before any session.
  '/passkey-ceremony',
  '/app-setup',
  '/connector-consent',
  // I-2219: where a local assistant's sign-in ends, once its loopback callback holds the token.
  appPath('/connected'),
  // F-0616 S2: the admin console's sign-in step; the page sends a signed-out visitor to sign in and back.
  '/console-sign-in',
  '/verify-session/:code',
  '/verify-email',
  appPath('/invite'),
  // F-0809 (EAI-1): a System Admin's invitation to the deployment; the token rides in the fragment.
  appPath('/join'),
  appPath('/artifact-invite'),
  appPath('/artifact-share-confirmations/:intentId'),
  '/forgot-password',
  '/reset-password',
  '/setup',
  '/claim',
  '/unsubscribe',
  // F-0557: the links a sign-in address change mails, opened with or without a session.
  '/confirm-sign-in-address',
  '/undo-sign-in-address',
  // F-0266 RL-1e: a reporter's own page, opened by the link in a message — no account, ever.
  appPath('/reporter'),
  // UXA-11: a signed link to look at one version and approve it — no account.
  appPath('/review'),
  // I-2298: a customer moment's link; the page sends a signed-out reader to sign in and back.
  appPath('/open'),
  // F-0766: the documented file address's lookup; the page sends a signed-out reader to sign in and back.
  appPath('/open/file'),
] as const;

function segmentsMatch(routePath: string, pathname: string): boolean {
  const routeSegments = routePath.split('/').filter(Boolean);
  const pathSegments = pathname.split('/').filter(Boolean);
  if (pathSegments.length < routeSegments.length) return false;
  return routeSegments.every(
    (segment, index) => segment.startsWith(':') || segment === pathSegments[index],
  );
}

/**
 * Whether `pathname` is one of the public routes above, a dynamic one of theirs (`/verify-session/…`),
 * or a page beneath one. Used to keep a signed-in-only poll from ever starting there (I-0942) and to
 * keep the global 401 handler from redirecting away from one.
 *
 * A query string or fragment on `pathname` is ignored, matching `window.location.pathname` (which
 * never carries either) as well as a caller that, like a test, hands the whole address.
 */
export function isPublicRoutePath(pathname: string): boolean {
  const bare = pathname.split('?')[0].split('#')[0];
  return PUBLIC_ROUTE_PATHS.some((route) => segmentsMatch(route, bare));
}
